/**
 * autismHistorySessionState.js
 *
 * Centralises the sessionStorage keys used by the Adult Autism History module
 * to persist a clinician's in-progress view state across navigation within a
 * browser-tab session:
 *   - navigation state (current section index + visited sections)
 *   - per-subsection clinician notes (so edits survive bouncing to the patient
 *     overview and back before the Firestore round-trip settles)
 *
 * WHY THIS EXISTS
 * ----------------
 * The notes cache can contain PII (clinician-typed notes about a patient).
 * Like the dashboard view-state namespace, it must be wiped on sign-out / token
 * expiry so a different clinician signing in on a shared machine never inherits
 * the previous one's notes. Keeping the key scheme and the clear helper in one
 * place mirrors src/utils/dashboardViewState.js and avoids stringly-typed keys
 * drifting between the component and the logout path.
 */

// Bump the version segment if the persisted shape ever changes incompatibly,
// so stale entries from older builds are ignored rather than mis-read.
const NOTES_PREFIX = 'adult-autism-history-notes-';
const NAV_PREFIX = 'adult-autism-history-nav-';

/** sessionStorage key holding the per-subsection notes map for a patient. */
export function autismHistoryNotesKey(patientId) {
  return `${NOTES_PREFIX}${patientId}`;
}

/** sessionStorage key holding the navigation state (index + visited) for a patient. */
export function autismHistoryNavKey(patientId) {
  return `${NAV_PREFIX}${patientId}`;
}

/**
 * Remove all Adult Autism History session state.
 *
 * Wipes every key under both prefixes (across all patients seen in this tab
 * session) rather than a single patient's, mirroring how clearDashboardViewState
 * clears its whole namespace on sign-out. Safe to call when sessionStorage is
 * unavailable.
 */
export function clearAutismHistorySessionState() {
  try {
    const keysToRemove = [];
    for (let i = 0; i < sessionStorage.length; i += 1) {
      const key = sessionStorage.key(i);
      if (key && (key.startsWith(NOTES_PREFIX) || key.startsWith(NAV_PREFIX))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => sessionStorage.removeItem(key));
  } catch {
    // sessionStorage unavailable (e.g. privacy mode) — nothing to clear.
  }
}
